import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { MyContext } from "../../Context/MyProvider";
import { toast } from "react-toastify";
import { FaBriefcase, FaHeart, FaTicketAlt, FaWpforms } from "react-icons/fa";
import { FiBell, FiHeadphones, FiLogOut, FiMapPin, FiRefreshCcw, FiSearch, FiShield, FiStar } from "react-icons/fi";
import { HiOutlineFilm, HiOutlineHome } from "react-icons/hi2";
import { MdOutlineAccountBalanceWallet, MdOutlineApps, MdOutlineEvent, MdOutlineLocalOffer, MdOutlineShoppingBag, MdOutlineSportsCricket } from "react-icons/md";
import { BsController } from "react-icons/bs";
import logoWithName from "../../Images/logowithname.png";
import DesktopUserChip from "./DesktopUserChip";
import { isUserLoggedIn, logoutUser } from "../../utils/auth";

const UserForm = () => {
    const navigate = useNavigate();
    const { slug } = useParams();
    const { BaseUrl, setIsLogin, setIsShowBtn } = useContext(MyContext);

    const [data, setData] = useState();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({});
    const [fileData, setFileData] = useState({});
    const [activeTooltip, setActiveTooltip] = useState(null);

    const [termsPopupOpen, setTermsPopupOpen] = useState(false);
    const [activeTermsField, setActiveTermsField] = useState(null);
    const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

    const FORM_DRAFT_KEY = `form_draft_${slug}`;
    const FORM_RETURN_KEY = "payment_form_return";
    const DONATION_OPTIONS = ["500", "1000", "2000", "5000"];

    const pinTimeout = useRef(null);

    useEffect(() => {
        document.body.classList.add("desktop-home-active");
        return () => {
            document.body.classList.remove("desktop-home-active");
        };
    }, []);

    const PIN_FIELD_KEYS = [
        "pin",
        "pincode",
        "zip",
        "zipcode",
        "postal",
        "postal_code",
        "postalcode",
    ];

    const allowOnlyNumbers = (e) => {
        if (
            e.key === "e" ||
            e.key === "E" ||
            e.key === "+" ||
            e.key === "-" ||
            e.key === "."
        ) {
            e.preventDefault();
        }
    };

    const saveDraft = (values) => {
        localStorage.setItem(FORM_DRAFT_KEY, JSON.stringify(values));
    };

    const clearDraft = () => {
        localStorage.removeItem(FORM_DRAFT_KEY);
        localStorage.removeItem(FORM_RETURN_KEY);
        localStorage.removeItem("payment_failed_order_id");
    };

    const isPinFieldName = (name = "") =>
        PIN_FIELD_KEYS.some((k) => String(name).toLowerCase().includes(k));

    const getAddressFieldName = (possibleKeys = []) => {
        const allAddressOptions =
            data?.formFields
                ?.filter((field) => field.type === "address")
                ?.flatMap((field) => field.options || []) || [];

        const matched = allAddressOptions.find((opt) =>
            possibleKeys.some((key) =>
                String(opt.name || "").toLowerCase().includes(key.toLowerCase())
            )
        );

        return matched?.name || null;
    };

    const getDefaultFormValues = (formFields = []) => {
        const initial = {};

        formFields.forEach((field) => {
            if (field.type === "checkbox") {
                initial[field.name] = [];
            } else if (field.type === "package") {
                initial[field.name] = field.options?.[0]?.amount || "";
            } else if (field.type === "donation") {
                initial[field.name] = "";
            } else if (field.type === "file") {
                initial[field.name] = field.is_multiple == 1 ? [] : "";
            } else if (field.type === "address") {
                field.options?.forEach((opt) => {
                    initial[opt.name] = "";
                });
            } else if (field.type === "user") {
                // Initialize with one empty user entry
                initial[field.name] = [{
                    name: "",
                    email: "",
                    password: "",
                    category: "",
                    mobile: ""
                }];
            } else if (field.type === "terms") {
                initial[field.name] = false;
            } else {
                initial[field.name] = "";
            }
        });

        return initial;
    };

    useEffect(() => {
        if (data?.formFields) {
            const initial = getDefaultFormValues(data.formFields);
            const savedDraft = localStorage.getItem(FORM_DRAFT_KEY);

            if (savedDraft) {
                try {
                    const parsed = JSON.parse(savedDraft);
                    const merged = { ...initial, ...parsed };

                    (data.formFields || []).forEach((field) => {
                        if (field.type === "file") {
                            merged[field.name] = field.is_multiple == 1 ? [] : "";
                        }
                    });

                    setFormData(merged);
                } catch {
                    setFormData(initial);
                }
            } else {
                setFormData(initial);
            }
        }
    }, [data, FORM_DRAFT_KEY]);

    const fetchAddressByPincode = async (pincode) => {
        const cleanPincode = String(pincode || "").trim();

        if (!cleanPincode) return;

        try {
            const res = await fetch(
                `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
                    cleanPincode
                )}&key=${process.env.REACT_APP_GOOGLE_MAPS_KEY}`
            );

            const json = await res.json();

            if (json.status !== "OK" || !json.results?.length) return;

            const postalCodeResult =
                json.results.find((item) =>
                    (item.types || []).includes("postal_code")
                ) || json.results[0];

            const components = postalCodeResult.address_components || [];

            const getComponent = (type) =>
                components.find((c) => (c.types || []).includes(type))?.long_name || "";

            const city =
                getComponent("locality") ||
                getComponent("postal_town") ||
                getComponent("administrative_area_level_3") ||
                getComponent("administrative_area_level_2") ||
                "";

            const state = getComponent("administrative_area_level_1") || "";
            const country = getComponent("country") || "";
            const postalCode = getComponent("postal_code") || cleanPincode;

            const cityFieldName = getAddressFieldName(["city", "district"]);
            const stateFieldName = getAddressFieldName(["state", "province", "region"]);
            const countryFieldName = getAddressFieldName(["country"]);
            const zipFieldName = getAddressFieldName([
                "pin",
                "pincode",
                "zip",
                "zipcode",
                "postal",
                "postal_code",
                "postalcode",
            ]);

            setFormData((prev) => {
                const updated = { ...prev };

                if (zipFieldName) updated[zipFieldName] = postalCode;
                if (cityFieldName) updated[cityFieldName] = city;
                if (stateFieldName) updated[stateFieldName] = state;
                if (countryFieldName) updated[countryFieldName] = country;

                saveDraft(updated);
                return updated;
            });
        } catch (error) {
            console.error("Pincode lookup failed:", error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => {
            const updated = { ...prev, [name]: value };
            saveDraft(updated);
            return updated;
        });

        if (isPinFieldName(name)) {
            clearTimeout(pinTimeout.current);

            pinTimeout.current = setTimeout(() => {
                fetchAddressByPincode(value);
            }, 500);
        }
    };

    const handleAddressFieldChange = (optName, value) => {
        setFormData((prev) => {
            const updated = {
                ...prev,
                [optName]: value,
            };
            saveDraft(updated);
            return updated;
        });

        if (isPinFieldName(optName)) {
            clearTimeout(pinTimeout.current);

            pinTimeout.current = setTimeout(() => {
                fetchAddressByPincode(value);
            }, 500);
        }
    };

    const handleUserFieldChange = (fieldName, userIndex, inputName, value) => {
        setFormData((prev) => {
            const updated = { ...prev };
            const users = [...(updated[fieldName] || [])];
            
            if (!users[userIndex]) {
                users[userIndex] = {
                    name: "",
                    email: "",
                    password: "",
                    category: "",
                    mobile: ""
                };
            }
            
            users[userIndex] = {
                ...users[userIndex],
                [inputName]: value
            };
            
            updated[fieldName] = users;
            saveDraft(updated);
            return updated;
        });
    };

    const removeUserEntry = (fieldName, userIndex) => {
        setFormData((prev) => {
            const updated = { ...prev };
            const users = [...(updated[fieldName] || [])];
            
            if (users.length > 1) {
                users.splice(userIndex, 1);
                updated[fieldName] = users;
                saveDraft(updated);
            }
            
            return updated;
        });
    };

    const addUserEntry = (fieldName) => {
        setFormData((prev) => {
            const updated = { ...prev };
            const users = [...(updated[fieldName] || [])];
            
            users.push({
                name: "",
                email: "",
                password: "",
                category: "",
                mobile: ""
            });
            
            updated[fieldName] = users;
            saveDraft(updated);
            return updated;
        });
    };

    const handleFileChange = (e, field) => {
        const files =
            field.is_multiple == 1
                ? Array.from(e.target.files || [])
                : e.target.files?.[0] || null;

        setFileData((prev) => ({
            ...prev,
            [field.name]: files,
        }));

        // formData me filename store mat karo
        setFormData((prev) => ({
            ...prev,
            [field.name]: field.is_multiple == 1 ? [] : "",
        }));
    };

    const validateRequiredFields = () => {
        for (const field of data?.formFields || []) {
            const value = formData[field.name];
            const currentFile = fileData[field.name];

            if (!field.required) continue;

            if (field.type === "checkbox") {
                if (!Array.isArray(value) || value.length === 0) {
                    toast.error(`Please select ${field.label}`);
                    return false;
                }
            } else if (field.type === "file") {
                if (field.is_multiple == 1) {
                    if (!Array.isArray(currentFile) || currentFile.length === 0) {
                        toast.error(`Please upload ${field.label}`);
                        return false;
                    }
                } else {
                    if (!(currentFile instanceof File)) {
                        toast.error(`Please upload ${field.label}`);
                        return false;
                    }
                }
            } else if (field.type === "url") {
                try {
                    const testValue =
                        value?.startsWith("http://") || value?.startsWith("https://")
                            ? value
                            : `https://${value}`;
                    new URL(testValue);
                } catch {
                    toast.error(`Please enter a valid ${field.label}`);
                    return false;
                }
            } else if (field.type === "address") {
                const requiredAddressFields = field.options || [];
                for (const opt of requiredAddressFields) {
                    if ((formData[opt.name] ?? "") === "") {
                        toast.error(`Please enter ${opt.lable || opt.name}`);
                        return false;
                    }
                }
            } else if (value === "" || value === null || value === undefined) {
                toast.error(`Please select ${field.label}`);
                return false;
            } else if (field.type === "terms") {
                if (field.required && !value) {
                    toast.error(`Please accept ${field.label || "Terms & Conditions"}`);
                    return false;
                }
            }
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!data?.form_id) {
            toast.error("Form ID missing");
            return;
        }

        if (!validateRequiredFields()) return;

        try {
            setLoading(true);

            const multipartData = new FormData();
            multipartData.append("form_id", String(data.form_id));

            let index = 0;

            (data?.formFields || []).forEach((field) => {
                // ✅ Handle address (flatten)
                if (field.type === "address") {
                    (field.options || []).forEach((opt) => {
                        multipartData.append(`data[${index}][name]`, opt.name ?? "");
                        multipartData.append(`data[${index}][type]`, opt.type ?? "text");
                        multipartData.append(`data[${index}][is_package]`, "0");
                        multipartData.append(`data[${index}][is_multiimage]`, "0");
                        multipartData.append(
                            `data[${index}][value]`,
                            formData[opt.name] ?? ""
                        );
                        index++;
                    });
                    return;
                }

                // ✅ Handle user (send as JSON array)
                if (field.type === "user") {
                    const users = formData[field.name] || [];
                    multipartData.append(`data[${index}][name]`, field.name ?? "");
                    multipartData.append(`data[${index}][type]`, field.type ?? "");
                    multipartData.append(`data[${index}][is_package]`, "0");
                    multipartData.append(`data[${index}][is_multiimage]`, "0");
                    multipartData.append(
                        `data[${index}][value]`,
                        JSON.stringify(users)
                    );
                    index++;
                    return;
                }

                // ✅ Normal fields
                multipartData.append(`data[${index}][name]`, field.name ?? "");
                multipartData.append(`data[${index}][type]`, field.type ?? "");
                multipartData.append(
                    `data[${index}][is_package]`,
                    String(field.is_package ?? 0)
                );
                multipartData.append(
                    `data[${index}][is_multiimage]`,
                    String(field.is_multiple ?? 0)
                );

                // ✅ File handling
                if (field.type === "file") {
                    if (field.is_multiple == 1) {
                        const files = fileData[field.name] || [];

                        if (files.length > 0) {
                            files.forEach((file) => {
                                multipartData.append(
                                    `data[${index}][value][]`, // ✅ IMPORTANT
                                    file
                                );
                            });
                        } else {
                            multipartData.append(`data[${index}][value][]`, "");
                        }

                    } else {
                        const files = fileData[field.name];
                        if (files instanceof File) {
                            console?.log(`Appending file for ${field.name}:`, files);
                            multipartData.append(`data[${index}][value]`, files);

                        } else {
                            multipartData.append(`data[${index}][value]`, "");
                        }
                    }
                }
                // ✅ Checkbox
                else if (field.type === "checkbox") {
                    const values = Array.isArray(formData[field.name])
                        ? formData[field.name]
                        : [];
                    multipartData.append(
                        `data[${index}][value]`,
                        values.join(",")
                    );
                } else if (field.type === "terms") {
                    multipartData.append(
                        `data[${index}][value]`,
                        formData[field.name] ? "1" : "0"
                    );
                }
                // ✅ Normal inputs
                else {
                    multipartData.append(
                        `data[${index}][value]`,
                        formData[field.name] ?? ""
                    );
                }

                index++;
            });

            const response = await fetch(`${BaseUrl}user_api/store_form.php`, {
                method: "POST",
                body: multipartData,
            });

            const res = await response.json();

            // if (res?.status === true) {
            if (res?.payment_required === true) {
                localStorage.removeItem("qr_link");

                saveDraft(formData);
                localStorage.setItem(
                    FORM_RETURN_KEY,
                    JSON.stringify({
                        slug,
                        order_id: res?.razorpay_order_id || "",
                    })
                );

                const options = {
                    key: res?.razorpay_key,
                    amount: res?.amount,
                    currency: "INR",
                    name: "Nabhiverse",
                    description: data?.title,
                    order_id: res?.razorpay_order_id,
                    handler: function () {
                        toast.success("Payment successful");
                        clearDraft();
                        setFileData({});
                        navigate(`/payment-status/${res?.razorpay_order_id}`);
                    },
                    modal: {
                        ondismiss: () => {
                            toast.info("Payment cancelled");
                            saveDraft(formData);
                            navigate(`/form/${slug}`, { replace: true });
                        },
                    },
                    theme: {
                        color: "#d2a347",
                    },
                };

                const rzp = new window.Razorpay(options);

                rzp.on("payment.failed", function () {
                    localStorage.setItem(
                        "payment_failed_order_id",
                        res?.razorpay_order_id || ""
                    );
                    saveDraft(formData);
                    navigate(`/payment-status/${res?.razorpay_order_id}`);
                });

                rzp.open();
            } else if (res?.payment_required === false) {
                const reset = getDefaultFormValues(data.formFields);
                setFormData(reset);
                setFileData({});
                localStorage.setItem("qr_link", res?.qr_link || "");
                clearDraft();
                navigate(`/payment-status/1`);
            }

        } catch (error) {
            toast.error(error?.message || "Submission failed");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const getFormData = async () => {
            try {
                const res = await axios.post(`${BaseUrl}user_api/form.php`, {
                    slug: slug,
                });

                if (res?.data?.status === true) {
                    setData(res.data.data);
                } else {
                    toast.error("Form not found");
                }
            } catch (error) {
                toast.error("Failed to load form");
            }
        };

        getFormData();
    }, [BaseUrl, slug]);

    const normalFields =
        data?.formFields?.filter(
            (field) => !(field.type === "package" && Number(field.eye_package) === 0)
        ) || [];

    const packageLastFields =
        data?.formFields?.filter(
            (field) => field.type === "package" && Number(field.eye_package) === 0
        ) || [];

    const orderedFields = [...normalFields, ...packageLastFields];
    const fullWidthFormFieldTypes = [
        "address",
        "appointment",
        "user",
        "file",
        "textarea",
        "radio",
        "checkbox",
        "terms",
        "package",
        "donation",
    ];
    const formMenuItems = [
        { label: "Home", icon: HiOutlineHome, path: "/" },
        { label: "Movies", icon: HiOutlineFilm },
        { label: "Events", icon: MdOutlineEvent, path: "/events" },
        { label: "Sports", icon: MdOutlineSportsCricket },
        { label: "Games", icon: BsController },
        { label: "Shopping", icon: MdOutlineShoppingBag, externalUrl: "https://shop.nabhiverse.com/" },
        { label: "Services", icon: MdOutlineApps },
        { label: "Offers", icon: MdOutlineLocalOffer },
        { label: "Bookings", icon: FaTicketAlt, path: "/booking" },
        { label: "Favorites", icon: FaHeart, path: "/favorite" },
        { label: "eWallet", icon: MdOutlineAccountBalanceWallet, path: "/wallet" },
        { label: "Forms", icon: FaWpforms, path: "/form-list", active: true },
        { label: "Business", icon: FaBriefcase, path: "/business-user" },
        { label: "More", icon: MdOutlineApps },
        { label: "Logout", icon: FiLogOut, action: "logout" },
    ];
    const mobileBottomMenuItems = [
        { label: "Home", icon: HiOutlineHome, path: "/" },
        { label: "Forms", icon: FaWpforms, path: "/form-list", active: true },
        { label: "Business", icon: FaBriefcase, path: "/business-user" },
        { label: "More", icon: MdOutlineApps, action: () => setIsMobileDrawerOpen(true) },
    ];
    const handleMenuClick = async (item) => {
        if (item.externalUrl) {
            window.location.href = item.externalUrl;
            setIsMobileDrawerOpen(false);
            return;
        }

        if (item.action === "logout") {
            const didLogout = await logoutUser({ navigate, setIsLogin, setIsShowBtn });

            if (didLogout) {
                toast.success("Logout successfully !!");
            }

            setIsMobileDrawerOpen(false);
            return;
        }

        if (item.path) {
            navigate(item.path);
            setIsMobileDrawerOpen(false);
        }
    };
    const shouldShowMenuItem = (item) =>
        !["Bookings", "Favorites", "eWallet", "Logout"].includes(item.label) ||
        isUserLoggedIn();

    return (
        <div className="nabhiverse_desktop_home form_list_layout user_form_layout">
            <aside className="desktop_home_sidebar">
                <div className="desktop_home_logo">
                    <img src={logoWithName} alt="Nabhiverse" />
                </div>
                <nav className="desktop_home_nav">
                    {formMenuItems.filter(shouldShowMenuItem).map((item) => {
                        const Icon = item.icon;
                        return (
                            <button
                                key={item.label}
                                className={`desktop_home_nav_item ${item.active ? "active" : ""}`}
                                type="button"
                                onClick={() => handleMenuClick(item)}
                            >
                                <Icon />
                                <span>{item.label}</span>
                            </button>
                        );
                    })}
                </nav>
                <div className="desktop_home_star_box">
                    <FiStar />
                    <span>nabi star</span>
                    <strong>Your Stars 620</strong>
                    <button type="button">View Benefits</button>
                </div>
                <div className="desktop_home_help_box">
                    <FiHeadphones />
                    <span>Need Help?</span>
                    <p>24x7 Support</p>
                    <button type="button">Contact Us</button>
                </div>
            </aside>

            <div
                className={`mobile_menu_overlay ${isMobileDrawerOpen ? "show" : ""}`}
                onClick={() => setIsMobileDrawerOpen(false)}
            ></div>
            <aside className={`mobile_menu_drawer ${isMobileDrawerOpen ? "show" : ""}`}>
                <div className="mobile_drawer_head">
                    <img src={logoWithName} alt="Nabhiverse" />
                    <button type="button" onClick={() => setIsMobileDrawerOpen(false)}>x</button>
                </div>
                <div className="mobile_drawer_body">
                    <nav className="mobile_drawer_nav">
                        {formMenuItems.filter(shouldShowMenuItem).map((item) => {
                            const Icon = item.icon;
                            return (
                                <button
                                    key={item.label}
                                    className={`desktop_home_nav_item ${item.active ? "active" : ""}`}
                                    type="button"
                                    onClick={() => handleMenuClick(item)}
                                >
                                    <Icon />
                                    <span>{item.label}</span>
                                </button>
                            );
                        })}
                    </nav>
                    <div className="desktop_home_star_box">
                        <FiStar />
                        <span>nabi star</span>
                        <strong>Your Stars 620</strong>
                        <button type="button">View Benefits</button>
                    </div>
                    <div className="desktop_home_help_box">
                        <FiHeadphones />
                        <span>Need Help?</span>
                        <p>24x7 Support</p>
                        <button type="button">Contact Us</button>
                    </div>
                </div>
            </aside>

            <main className="desktop_home_main form_list_main user_form_main">
                <header className="desktop_home_topbar form_list_topbar">
                    <div className="mobile_header_logo">
                        <img src={logoWithName} alt="Nabhiverse" />
                    </div>
                    <div className="desktop_search_box">
                        <FiSearch />
                        <input
                            type="text"
                            placeholder="Search for apps, services, movies, events and more..."
                            readOnly
                        />
                    </div>
                    <div className="desktop_location"><FiMapPin /> Bengaluru</div>
                    <DesktopUserChip />
                    <button className="desktop_bell_btn" type="button" aria-label="Notifications">
                        <FiBell />
                        <span>3</span>
                    </button>
                </header>

                <section className="user_form_shell">
                    {data?.banner_image && (
                        <div className="user_form_banner">
                            <img src={data.banner_image} alt="banner" />
                        </div>
                    )}

                    <div className="user_form_panel_wrap">
                        <div className="user_form_card">
                    {data?.logo && (
                        <div className="flex justify-center mb-4">
                            <img
                                src={data.logo}
                                alt="logo"
                                className="h-16 object-contain"
                            />
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <h2 className="text-center text-[22px] sm:text-[24px] md:text-[26px] font-bold text-[#fff] mt-4 mb-6 tracking-[0.5px]">
                            {data?.title}
                        </h2>

                        <div className="user_form_fields_grid">
                        {orderedFields.map((field, index) => {
                            const options = field.options || [];
                            const isFullWidthField = fullWidthFormFieldTypes.includes(field.type);

                            return (
                                <div
                                    className={`user_form_field ${isFullWidthField ? "user_form_field_full" : ""}`}
                                    key={`${field.name}-${index}`}
                                >
                                    <label
                                        htmlFor={field.name}
                                        className="block text-sm font-medium text-white capitalize"
                                    >
                                        {field.label}
                                        {field.required && <span className="text-red-500"> *</span>}
                                    </label>

                                    <div className="mt-2 relative">
                                        {((field?.type === "text" && field?.icon) ||
                                            (field?.type === "number" && field?.icon)) && (
                                                <img
                                                    src={field.icon}
                                                    alt="icon"
                                                    className="absolute left-4 top-1/2 transform -translate-y-[30%] w-5 h-auto object-contain"
                                                />
                                            )}

                                        {["text", "number", "email", "date", "time", "url","referral"].includes(
                                            field.type
                                        ) && (
                                                <input
                                                    id={field.name}
                                                    name={field.name}
                                                    type={field.type}
                                                    placeholder={
                                                        ["date", "time"].includes(field.type)
                                                            ? ""
                                                            : field.placeholder
                                                                ? field.placeholder
                                                                : field.type === "url"
                                                                    ? "Enter valid URL"
                                                                    : `Enter ${field.name}`
                                                    }
                                                    value={formData[field.name] ?? ""}
                                                    onChange={handleChange}
                                                    onKeyDown={
                                                        field.type === "number" ? allowOnlyNumbers : undefined
                                                    }
                                                    inputMode={
                                                        field.type === "number"
                                                            ? "numeric"
                                                            : field.type === "url"
                                                                ? "url"
                                                                : undefined
                                                    }
                                                    pattern={field.type === "number" ? "[0-9]*" : undefined}
                                                    required={field.required}
                                                    className={`mt-2 w-full rounded-full border-2 border-gray-300
                                                ${field?.icon ? "pl-[38px] pr-[10px]" : "px-4"}
                                                py-3 text-gray-800
                                                placeholder-gray-400
                                                focus:outline-none focus:border-blue-500
                                                focus:ring-2 focus:ring-blue-200
                                                transition`}
                                                />
                                            )}

                                        {field.type === "address" && (() => {
                                            const addressOptions = field.options || [];

                                            const zipOptions = addressOptions.filter((opt) =>
                                                isPinFieldName(opt.name)
                                            );

                                            const otherOptions = addressOptions.filter(
                                                (opt) => !isPinFieldName(opt.name)
                                            );

                                            const sortedAddressOptions = [...zipOptions, ...otherOptions];

                                            return (
                                                <div className="mt-3">
                                                    <p className="mb-3 text-[13px] text-[#8a6d3b] bg-[#fff7e6] border border-[#f3d9a4] rounded-xl px-4 py-2">
                                                        Fill zip code, country state city auto fill.
                                                    </p>

                                                    <div className="grid grid-cols-1 gap-3">
                                                        {sortedAddressOptions.map((opt, idx) => {
                                                            const lowerName = String(opt.name || "").toLowerCase();

                                                            const isAutoReadonly =
                                                                lowerName.includes("city") ||
                                                                lowerName.includes("state") ||
                                                                lowerName.includes("country") ||
                                                                lowerName.includes("district") ||
                                                                lowerName.includes("province") ||
                                                                lowerName.includes("region");

                                                            return (
                                                                <input
                                                                    key={idx}
                                                                    type={opt.type || "text"}
                                                                    placeholder={opt.lable}
                                                                    value={formData[opt.name] || ""}
                                                                    onChange={(e) =>
                                                                        handleAddressFieldChange(opt.name, e.target.value)
                                                                    }
                                                                    onKeyDown={
                                                                        isPinFieldName(opt.name)
                                                                            ? allowOnlyNumbers
                                                                            : undefined
                                                                    }
                                                                    inputMode={
                                                                        isPinFieldName(opt.name) ? "numeric" : undefined
                                                                    }
                                                                    readOnly={isAutoReadonly}
                                                                    disabled={isAutoReadonly}
                                                                    className={`w-full rounded-full border-2 px-4 py-3
                                                                    ${isAutoReadonly
                                                                            ? "border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed"
                                                                            : "border-gray-300 bg-white text-gray-800"
                                                                        }`}
                                                                />
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {field.type === "appointment" && (
                                            <div className="mt-3 grid grid-cols-1 gap-3">
                                                {field.options?.map((slot, i) => {
                                                    const value = `${slot.start}__${slot.end}`;

                                                    return (
                                                        <div
                                                            key={i}
                                                            onClick={() =>
                                                                setFormData((prev) => {
                                                                    const updated = {
                                                                        ...prev,
                                                                        [field.name]: value,
                                                                    };
                                                                    saveDraft(updated);
                                                                    return updated;
                                                                })
                                                            }
                                                            className={`cursor-pointer border-2 rounded-xl p-4 transition
                                                            ${formData[field.name] === value
                                                                    ? "border-[#d2a347] bg-[#fff7e6]"
                                                                    : "border-gray-300"
                                                                }`}
                                                        >
                                                            <p className="text-sm font-medium">
                                                                {new Date(slot.start).toLocaleString()}
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                to {new Date(slot.end).toLocaleString()}
                                                            </p>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {field.type === "user" && (() => {
                                            const users = formData[field.name] || [];
                                            const userOptions = field.options || [];

                                            return (
                                                <div className="mt-3">
                                                    <div className="space-y-4">
                                                        {users.map((user, userIndex) => (
                                                            <div key={userIndex} className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                                                                <div className="flex justify-between items-center mb-3">
                                                                    <h4 className="text-sm font-medium text-gray-700">
                                                                        Profile {userIndex + 1}
                                                                    </h4>
                                                                    {users.length > 1 && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => removeUserEntry(field.name, userIndex)}
                                                                            className="text-red-500 hover:text-red-700 text-sm"
                                                                        >
                                                                            Remove
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                
                                                                <div className="grid grid-cols-1 gap-3">
                                                                    {userOptions.map((option, optIndex) => (
                                                                        <input
                                                                            key={optIndex}
                                                                            type={option.type || "text"}
                                                                            placeholder={option.lable || option.name}
                                                                            value={user[option.name] || ""}
                                                                            onChange={(e) =>
                                                                                handleUserFieldChange(field.name, userIndex, option.name, e.target.value)
                                                                            }
                                                                            onKeyDown={
                                                                                option.type === "number" ? allowOnlyNumbers : undefined
                                                                            }
                                                                            inputMode={
                                                                                option.type === "number" ? "numeric" : undefined
                                                                            }
                                                                            className="w-full rounded-full border-2 border-gray-300 px-4 py-3 text-gray-800 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                                                                        />
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    
                                                    <button
                                                        type="button"
                                                        onClick={() => addUserEntry(field.name)}
                                                        className="mt-3 flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                                                    >
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                        </svg>
                                                        Add Another Profile
                                                    </button>
                                                </div>
                                            );
                                        })()}

                                        {field.type === "file" && (
                                            <div className="mt-2">
                                                <input
                                                    id={field.name}
                                                    name={field.name}
                                                    type="file"
                                                    multiple={field.is_multiple == 1}
                                                    accept={field.accept || "image/*"}
                                                    onChange={(e) => handleFileChange(e, field)}
                                                    required={
                                                        field.required &&
                                                        (field.is_multiple == 1
                                                            ? !Array.isArray(formData[field.name]) ||
                                                            formData[field.name].length === 0
                                                            : !formData[field.name])
                                                    }
                                                    className="w-full rounded-lg border-2 border-gray-300 px-4 py-3 text-gray-800
                                                    file:mr-4 file:rounded-full file:border-0 file:bg-[#d2a347]
                                                    file:px-4 file:py-2 file:text-white file:cursor-pointer"
                                                />

                                                {/* {field.is_multiple == 1 ? ( */}
                                                {Array.isArray(formData[field.name]) &&
                                                    formData[field.name].length > 0 && (
                                                        <div className="mt-2">
                                                            <p className="text-sm text-gray-500 mb-1">
                                                                Previously selected files:
                                                            </p>

                                                            <div className="space-y-1">
                                                                {formData[field.name].map((fileName, fileIndex) => (
                                                                    <p key={fileIndex} className="text-sm text-gray-500 break-all">
                                                                        {fileName}
                                                                    </p>
                                                                ))}
                                                            </div>

                                                            <p className="text-xs text-red-500 mt-1">
                                                                ⚠️ Please re-upload files before submitting
                                                            </p>
                                                        </div>
                                                    )}
                                                {/* ) : (
                                                    formData[field.name] && (
                                                        <div className="mt-2">
                                                            <p className="text-sm text-gray-500 break-all">
                                                                Previously selected: {formData[field.name]}
                                                            </p>
                                                            <p className="text-xs text-red-500 mt-1">
                                                                ⚠️ Please re-upload file before submitting
                                                            </p>
                                                        </div>
                                                    )
                                                )} */}

                                                {field.is_multiple == 1 ? (
                                                    Array.isArray(fileData[field.name]) &&
                                                    fileData[field.name].length > 0 && (
                                                        <div className="mt-3 flex flex-wrap gap-3">
                                                            {fileData[field.name]
                                                                .filter((file) => file?.type?.startsWith("image/"))
                                                                .map((file, previewIndex) => (
                                                                    <img
                                                                        key={previewIndex}
                                                                        src={URL.createObjectURL(file)}
                                                                        alt={`preview-${previewIndex}`}
                                                                        className="h-24 w-24 rounded-lg object-cover border"
                                                                    />
                                                                ))}
                                                        </div>
                                                    )
                                                ) : (
                                                    fileData[field.name] &&
                                                    fileData[field.name]?.type?.startsWith("image/") && (
                                                        <img
                                                            src={URL.createObjectURL(fileData[field.name])}
                                                            alt="preview"
                                                            className="mt-3 h-24 w-24 rounded-lg object-cover border"
                                                        />
                                                    )
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {field.type === "textarea" && (
                                        <textarea
                                            name={field.name}
                                            value={formData[field.name] ?? ""}
                                            onChange={handleChange}
                                            className="mt-3 w-full rounded-lg border-2 border-gray-300
                                            px-4 py-3 text-gray-800
                                            placeholder-gray-400
                                            focus:outline-none focus:border-blue-500
                                            focus:ring-2 focus:ring-blue-200
                                            transition"
                                        />
                                    )}

                                    {field.type === "select" && (
                                        <select
                                            name={field.name}
                                            value={formData[field.name] ?? ""}
                                            onChange={handleChange}
                                            className="mt-2 w-full rounded-full border-2 border-gray-300
                                            px-4 py-3 text-gray-800
                                            placeholder-gray-400
                                            focus:outline-none focus:border-blue-500
                                            focus:ring-2 focus:ring-blue-200
                                            transition form-wrapper"
                                        >
                                            <option value="">Select option</option>
                                            {options?.map((opt) => (
                                                <option
                                                    key={opt}
                                                    value={opt}
                                                    className="whitespace-normal"
                                                >
                                                    {opt}
                                                </option>
                                            ))}
                                        </select>
                                    )}

                                    {field.type === "radio" && (
                                        <div className="flex gap-4 flex-wrap mt-2">
                                            {field.options.map((opt) => (
                                                <label
                                                    key={opt}
                                                    className="flex gap-2 items-center text-white"
                                                >
                                                    <input
                                                        type="radio"
                                                        name={field.name}
                                                        value={opt}
                                                        checked={formData[field.name] === opt}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            setFormData((prev) => {
                                                                const updated = { ...prev, [field.name]: value };
                                                                saveDraft(updated);
                                                                return updated;
                                                            });
                                                        }}
                                                    />
                                                    {opt}
                                                </label>
                                            ))}
                                        </div>
                                    )}

                                    {field.type === "checkbox" && (
                                        <div className="flex gap-4 flex-wrap mt-2">
                                            {field.options.map((opt) => (
                                                <label
                                                    key={opt}
                                                    className="flex gap-2 items-center text-white"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={formData[field.name]?.includes(opt) || false}
                                                        onChange={(e) => {
                                                            const checked = e.target.checked;
                                                            setFormData((prev) => {
                                                                const values = prev[field.name] || [];
                                                                const updated = {
                                                                    ...prev,
                                                                    [field.name]: checked
                                                                        ? [...values, opt]
                                                                        : values.filter((v) => v !== opt),
                                                                };
                                                                saveDraft(updated);
                                                                return updated;
                                                            });
                                                        }}
                                                    />
                                                    {opt}
                                                </label>
                                            ))}
                                        </div>
                                    )}

                                    {field.type === "terms" && (
                                        <div className="mt-2">
                                            <label className="flex items-start gap-2 text-gray-600">
                                                <input
                                                    type="checkbox"
                                                    checked={!!formData[field.name]}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;

                                                        setFormData((prev) => {
                                                            const updated = {
                                                                ...prev,
                                                                [field.name]: checked,
                                                            };
                                                            saveDraft(updated);
                                                            return updated;
                                                        });
                                                    }}
                                                    className="mt-1"
                                                />

                                                <span className="text-sm leading-6">
                                                    I accept the{" "}
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setActiveTermsField(field);
                                                            setTermsPopupOpen(true);
                                                        }}
                                                        className="text-[#d2a347] underline font-medium hover:text-blue-700"
                                                    >
                                                        Terms & Conditions
                                                    </button>
                                                </span>
                                            </label>

                                            {field.required && !formData[field.name] && (
                                                <p className="mt-1 text-xs text-red-500">
                                                    Please accept Terms & Conditions
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    {field.type === "package" && (
                                        <div className="mt-3 grid grid-cols-2 gap-3">
                                            {field.options.map((pkg, i) => {
                                                const tooltipKey = `${field.name}_${i}_${pkg.amount}`;
                                                const showTooltipButton = Number(field.eye_package) === 1;

                                                return (
                                                    <div
                                                        key={i}
                                                        onClick={() =>
                                                            setFormData((prev) => {
                                                                const updated = {
                                                                    ...prev,
                                                                    [field.name]: pkg.amount,
                                                                };
                                                                saveDraft(updated);
                                                                return updated;
                                                            })
                                                        }
                                                        className={`cursor-pointer border-2 rounded-xl p-4 transition relative
                                                        ${formData[field.name] === pkg.amount
                                                                ? "border-[#d2a347] bg-[#fff7e6] "
                                                                : "border-gray-300"
                                                            }`}
                                                    >
                                                        <div className="flex items-start justify-between gap-2 relative">
                                                            <h4 className={`${formData[field.name] === pkg.amount ? 'text-black' : 'text-white'} font-semibold `}>
                                                                {pkg.title}
                                                            </h4>

                                                            {showTooltipButton && pkg.desc && (
                                                                <div
                                                                    className="relative shrink-0"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <button
                                                                        type="button"
                                                                        onClick={() =>
                                                                            setActiveTooltip((prev) =>
                                                                                prev === tooltipKey ? null : tooltipKey
                                                                            )
                                                                        }
                                                                        className="w-5 h-5 flex items-center justify-center rounded-full border border-gray-400 text-[12px] font-bold text-gray-600 hover:bg-gray-100"
                                                                    >
                                                                        !
                                                                    </button>

                                                                    <div
                                                                        className={`absolute right-0 top-7 z-20 w-56 rounded-lg bg-black text-white text-xs px-3 py-2 shadow-lg
                                                                        ${activeTooltip === tooltipKey
                                                                                ? "block"
                                                                                : "hidden"
                                                                            }`}
                                                                    >
                                                                        {pkg.desc}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {!showTooltipButton && pkg.desc && (
                                                            <p className="mt-2 text-sm text-gray-500">
                                                                {pkg.desc}
                                                            </p>
                                                        )}

                                                        <p className="mt-2 font-bold text-[#d2a347]">
                                                            ₹{pkg.amount}
                                                        </p>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {field.type === "donation" && (
                                        <div className="mt-3">
                                            <input
                                                id={field.name}
                                                name={field.name}
                                                type="text"
                                                placeholder={field.placeholder || "Enter donation amount"}
                                                value={formData[field.name] ?? ""}
                                                onChange={(e) => {
                                                    const onlyNumbers = e.target.value.replace(/\D/g, "");
                                                    setFormData((prev) => {
                                                        const updated = {
                                                            ...prev,
                                                            [field.name]: onlyNumbers,
                                                        };
                                                        saveDraft(updated);
                                                        return updated;
                                                    });
                                                }}
                                                onKeyDown={allowOnlyNumbers}
                                                inputMode="numeric"
                                                className="w-full rounded-full border-2 border-gray-300 px-4 py-3 text-gray-800
                                                placeholder-gray-400 focus:outline-none focus:border-blue-500
                                                focus:ring-2 focus:ring-blue-200 transition"
                                            />

                                            <div className="flex flex-wrap gap-3 mt-3">
                                                {DONATION_OPTIONS.map((amount) => (
                                                    <button
                                                        key={amount}
                                                        type="button"
                                                        onClick={() =>
                                                            setFormData((prev) => {
                                                                const updated = {
                                                                    ...prev,
                                                                    [field.name]: amount,
                                                                };
                                                                saveDraft(updated);
                                                                return updated;
                                                            })
                                                        }
                                                        className={`px-3 py-1 rounded-full text-[0.85rem] font-medium transition
                                                        ${formData[field.name] === amount
                                                                ? "border-[#d2a347] bg-[#fff7e6] text-[#d2a347]"
                                                                : "border-gray-300 bg-[#00000008] text-gray-700 hover:border-[#d2a347]"
                                                            }`}
                                                    >
                                                        ₹{amount}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full mt-2 rounded-full bg-[#d2a347] py-3 text-white font-semibold disabled:opacity-60"
                        >
                            {loading ? "Submitting..." : "Submit"}
                        </button>
                    </form>
                </div>
            </div>
                </section>

                <footer className="desktop_home_footer form_list_footer">
                    <img src={logoWithName} alt="Nabhiverse" />
                    <span><FiShield /> Fast & Secure Payments</span>
                    <span><FiRefreshCcw /> Easy Returns</span>
                    <span><FiHeadphones /> 24x7 Support</span>
                    <p>© 2026 Nabhiverse. All Rights Reserved.</p>
                </footer>
            </main>

            <nav className="mobile_bottom_menu">
                {mobileBottomMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                        <button
                            key={item.label}
                            type="button"
                            className={item.active ? "active" : ""}
                            onClick={() => item.action ? item.action() : navigate(item.path)}
                        >
                            <Icon />
                            <span>{item.label}</span>
                        </button>
                    );
                })}
            </nav>

            {termsPopupOpen && activeTermsField && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4">
                    <div className="bg-white w-full max-w-2xl rounded-2xl shadow-xl overflow-hidden">
                        <div className="flex items-center justify-between px-5 py-4 border-b">
                            <h3 className="text-lg font-semibold text-gray-800">
                                {activeTermsField.label || "Terms & Conditions"}
                            </h3>

                            <button
                                type="button"
                                onClick={() => {
                                    setTermsPopupOpen(false);
                                    setActiveTermsField(null);
                                }}
                                className="text-gray-500 hover:text-black text-xl"
                            >
                                ×
                            </button>
                        </div>

                        <div className="p-5 max-h-[70vh] overflow-y-auto">
                            <div
                                className="prose max-w-none text-sm text-gray-700"
                                dangerouslySetInnerHTML={{
                                    __html: activeTermsField.terms || "<p>No terms available.</p>",
                                }}
                            />
                        </div>

                        <div className="px-5 py-4 border-t flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => {
                                    setTermsPopupOpen(false);
                                    setActiveTermsField(null);
                                }}
                                className="px-4 py-2 rounded-full border border-gray-300 text-gray-700"
                            >
                                Close
                            </button>

                            <button
                                type="button"
                                onClick={() => {
                                    setFormData((prev) => {
                                        const updated = {
                                            ...prev,
                                            [activeTermsField.name]: true,
                                        };
                                        saveDraft(updated);
                                        return updated;
                                    });

                                    setTermsPopupOpen(false);
                                    setActiveTermsField(null);
                                }}
                                className="px-4 py-2 rounded-full bg-[#d2a347] text-white"
                            >
                                Accept
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserForm;
